import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import { icon_chevron_right } from '@gn-audio/icons';
import {
  AfterSubmitFormMessage,
  CrmFormEntity,
  FormMessageType,
  GtmEventType,
} from '@gn-audio/jss-core';

import { AlignmentEnum } from '../../types';
import {
  handleFormMessage,
  postFormScript,
  resolveColorProperty,
  TailwindColor,
  TrackingContext,
} from '../../utils';
import { useLoginType } from '../../utils/hooks/useLoginType';
import { Button } from '../Button/Button';
import { JabraIcon } from '../JabraIcon/JabraIcon';
import RichText from '../RichText/RichText';
import { FormWrapperFields } from './FormWrapperTypes';

export const FormWrapper = (fields: FormWrapperFields) => {
  const {
    content,
    link,
    formIframeSrc,
    backgroundColor,
    loginType,
    formAlignment,
    formWidth,
    formScript,
  } = fields || {};

  const iframe = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<string | undefined>(undefined);
  const [iframeId, setIframeId] = useState();
  const [eventListenersLoaded, setEventListenersLoaded] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { gtmEvent } = useContext(TrackingContext);
  const { isVisible } = useLoginType(loginType);
  const router = useRouter();
  const cleanFormScript = (formScript?.value ?? '').trim();

  useEffect(() => {
    const setHeight = (event: MessageEvent) => {
      return setIframeHeight(`${event.data.height}px`);
    };
    const setId = (event: MessageEvent) => {
      return setIframeId(event.data.formId);
    };
    const redirectToUrl = (event: MessageEvent) => router.push(event.data.url);
    const emitGtmEvent = (event: MessageEvent<AfterSubmitFormMessage>) => {
      if (event.data.crmEntity !== CrmFormEntity.Lead) return;

      const { crmEntity, crmCampaignId, crmCampaignName, crmTopic, crmContact } = event.data;
      gtmEvent({
        event: GtmEventType.JssLeadFormSubmitted,
        crmEntity,
        crmCampaignId,
        crmCampaignName,
        crmTopic,
        crmContact,
      });
    };
    const sendFormScript = () => postFormScript(iframe, cleanFormScript);

    const formHeightHandler = handleFormMessage(FormMessageType.SetHeight, setHeight);
    const formIdHandler = handleFormMessage(FormMessageType.SetFormId, setId);
    const formRedirectHandler = handleFormMessage(FormMessageType.OnRedirect, redirectToUrl);
    const formSubmitHandler = handleFormMessage(FormMessageType.AfterSubmit, emitGtmEvent);
    const formReadyHandler = handleFormMessage(FormMessageType.FormReady, sendFormScript);

    window.addEventListener('message', formReadyHandler);
    window.addEventListener('message', formHeightHandler);
    window.addEventListener('message', formIdHandler);
    window.addEventListener('message', formRedirectHandler);
    window.addEventListener('message', formSubmitHandler);

    if (!eventListenersLoaded) setEventListenersLoaded(true);

    return () => {
      window.removeEventListener('message', formReadyHandler);
      window.removeEventListener('message', formHeightHandler);
      window.removeEventListener('message', formIdHandler);
      window.removeEventListener('message', formRedirectHandler);
      window.removeEventListener('message', formSubmitHandler);
    };
  }, [gtmEvent, router, eventListenersLoaded, cleanFormScript]);

  if (!isVisible) return null;

  const alignment = formAlignment?.value ?? AlignmentEnum.right;

  const bgColor = backgroundColor?.value as TailwindColor;
  const bgColorHex = resolveColorProperty(bgColor);
  const borderRadiusStyle: CSSProperties = {
    borderTopLeftRadius: 'var(--component-top-left-border-radius, inherit)',
    borderTopRightRadius: 'var(--component-top-right-border-radius, inherit)',
  };
  const isLoading = !eventListenersLoaded || !iframeLoaded;

  return (
    <div className="bg-theme 2xl:px-auto container-fluid px-4 sm:px-12 md:px-16">
      <div className="container rounded-3xl p-0">
        <section
          className={classNames(
            'grid gap-4 rounded-3xl md:gap-x-12 lg:gap-x-16 lg:pt-0 2xl:gap-x-24',
            { 'lg:grid-cols-2': alignment !== AlignmentEnum.center }
          )}
          style={{ ...borderRadiusStyle, backgroundColor: bgColorHex }}
        >
          <div
            className={classNames(
              'lg:basis-2/5 lg:pt-0 xl:basis-6/12',
              alignment !== AlignmentEnum.center && ['mb-5 basis-full md:basis-1/2'],
              {
                'order-1 md:order-2': alignment === AlignmentEnum.left,
                'order-1': alignment === AlignmentEnum.right,
                'pb-4': alignment === AlignmentEnum.center,
              }
            )}
          >
            <RichText text={content?.value} />
            {!!link?.value?.href && (
              <Button
                className="mt-6 flex items-center"
                size="m"
                variant="secondary"
                icon={<JabraIcon className="size-4" Icon={icon_chevron_right} />}
                iconPlacement="right"
                asLink
                href={link.value.href}
                target={link.value.target}
                title={link.value.title}
              >
                {link.value.text}
              </Button>
            )}
          </div>
          <div
            className={classNames(
              'basis-full lg:basis-3/5 xl:basis-6/12',
              alignment !== AlignmentEnum.center && ['md:!basis-1/2'],
              {
                'order-2 lg:order-1 lg:pr-0': alignment === AlignmentEnum.left,
                'order-2 lg:pl-0': alignment === AlignmentEnum.right,
                'flex justify-center': alignment === AlignmentEnum.center,
                flex: isLoading,
              }
            )}
          >
            <div
              className={classNames(
                { 'border border-gray-100': bgColor },
                'w-full overflow-hidden rounded-3xl'
              )}
              style={{
                maxWidth:
                  alignment === AlignmentEnum.center && formWidth?.value
                    ? formWidth.value
                    : undefined,
              }}
            >
              {isLoading && <div className="animate-glow h-full" />}
              {eventListenersLoaded && (
                <iframe
                  id={iframeId}
                  ref={iframe}
                  src={formIframeSrc?.value?.href}
                  title={formIframeSrc?.value?.title}
                  height={iframeHeight}
                  className={classNames('w-full', { 'opacity-0': !iframeLoaded })}
                  onLoad={() => setIframeLoaded(true)}
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
